import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { compact } from 'lodash-es';
import { TableList, Typography, TextButton, InfoIcon, useIsMounted } from 'seagull';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { getURLWithFullPageInvoice } from '~/components/FullPageInvoice/FullPageInvoice.utils';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { wait } from '~/infra/utils';
import { timeModel } from '~/models';
import { PayoutAccountingIntegrationStatus } from '~/pages/PayoutsDashboard/PayoutAccountingIntegrationStatus/PayoutAccountingIntegrationStatus';
import PayoutAccountingSection from '~/pages/PayoutsDashboard/PayoutsQboSection/PayoutAccountingSection';
import { selectors } from '~/store/state-selectors';
import { useAppConfig, useStore, useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import PayoutFailedCallout from '../PayoutFailureMessage/PayoutFailedCallout';
import styles from './PayoutsDashboardRow.module.scss';
import { getStatusSkin, getStatusText } from './PayoutsDashboardRow.utils';
const POLLING_TIMEOUT = 2 * 60 * 1000; // 2min in ms
function AmountDisplay(props) {
    if (!props.amount) return null;
    return /*#__PURE__*/ _jsx(Typography, {
        ellipsis: true,
        skin: "secondary",
        testId: props.testId,
        children: props.amount.display()
    });
}
function FeesCell(param) {
    let { invoice } = param;
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.fees,
        "data-testid": "fees-".concat(invoice.id),
        children: [
            /*#__PURE__*/ _jsx(AmountDisplay, {
                amount: invoice.totalFees
            }),
            !invoice.totalFees.isZero() && /*#__PURE__*/ _jsx(InfoIcon, {
                testId: "fees-tooltip-".concat(invoice.id),
                info: /*#__PURE__*/ _jsxs("div", {
                    className: styles.feesTooltip,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            skin: "invertedStandard",
                            weight: "bold",
                            children: "Transaction fees"
                        }),
                        !invoice.fee.isZero() && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "fees-tooltip-anchor-fee-".concat(invoice.id),
                            children: [
                                invoice.fee.display(),
                                " Anchor"
                            ]
                        }),
                        !invoice.processingFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "fees-tooltip-processing-fee-".concat(invoice.id),
                            children: [
                                invoice.processingFee.display(),
                                " Credit card"
                            ]
                        }),
                        !invoice.serviceFeeRefund.isZero() && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "fees-tooltip-service-fee-refund-".concat(invoice.id),
                            children: [
                                invoice.serviceFeeRefund.neg().display(),
                                " Anchor fee refund"
                            ]
                        }),
                        !invoice.customerProcessingFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "fees-tooltip-customer-processing-fee-".concat(invoice.id),
                            children: [
                                invoice.customerProcessingFee.display(),
                                " Credit card fees returned to the client"
                            ]
                        }),
                        !invoice.disputeFee.isZero() && /*#__PURE__*/ _jsxs("div", {
                            "data-testid": "fees-tooltip-dispute-fee-".concat(invoice.id),
                            children: [
                                invoice.disputeFee.display(),
                                " chargebacks fee for a dispute received on invoice",
                                ' ',
                                invoice.formattedNumber
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
export default function PayoutsDashboardRow(props) {
    const { index, invoiceHeaders } = props;
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
    const { api } = useStoreActions();
    const isMounted = useIsMounted();
    const { qboPollingInterval } = useAppConfig();
    const [hasError, setHasError] = useState(false);
    const testId = "payments-row-".concat(index);
    const { XERO_INTEGRATION } = useFeatureFlags();
    const [payout, setPayout] = useState(props.payout);
    useEffect(()=>setPayout(props.payout), [
        props.payout
    ]);
    const columnDefs = [
        {
            width: showQboIntegration || showXeroIntegration ? 160 : 120
        },
        {
            width: 80
        },
        {
            width: [
                140,
                180
            ]
        },
        {
            width: [
                90,
                100
            ]
        },
        {
            width: [
                90,
                100
            ]
        },
        {
            width: 100,
            hAlign: 'end'
        },
        {
            width: 80,
            hAlign: 'end'
        },
        {
            width: 110,
            hAlign: 'end'
        }
    ];
    const headerItems = invoiceHeaders.map((text)=>({
            value: text
        }));
    async function reloadPayout(accounting) {
        const fetchPayout = async ()=>{
            return api.payout.getById(payout.id()).catch((err)=>{
                isMounted() && setHasError(true);
                throw err;
            });
        };
        const start = Date.now();
        let data = await fetchPayout();
        // Keep polling until the invoice reaches a stable integration status.
        while(isMounted() && data.accountingIntegration(accounting) && !data.accountingIntegration(accounting).isStableStatus() && Date.now() - start < POLLING_TIMEOUT){
            await wait(qboPollingInterval);
            data = await fetchPayout();
        }
        isMounted() && setPayout(data);
        return data;
    }
    if (hasError) {
        throw makeSentryIgnoredError('Payout fetch error');
    }
    return /*#__PURE__*/ _jsx(TableList.Row, {
        as: "button",
        testId: testId,
        "data-heapid": "payment",
        rowId: payout.id(),
        items: compact([
            {
                value: /*#__PURE__*/ _jsxs("div", {
                    className: styles.payoutIdCell,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            weight: "light",
                            className: styles.payoutId,
                            children: payout.formattedNumber()
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.payoutStatus,
                            children: [
                                showQboIntegration && /*#__PURE__*/ _jsx(PayoutAccountingIntegrationStatus, {
                                    accounting: Accounting.QBO,
                                    testId: "".concat(testId, "-qbo"),
                                    integration: payout.qboIntegration()
                                }),
                                showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/ _jsx(PayoutAccountingIntegrationStatus, {
                                    accounting: Accounting.Xero,
                                    testId: "".concat(testId, "-xero"),
                                    integration: payout.xeroIntegration()
                                })
                            ]
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "light",
                    children: payout.formattedCreatedAt()
                })
            },
            {
                value: null
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    capitalize: true,
                    testId: "".concat(testId, "-status"),
                    skin: getStatusSkin(payout),
                    children: getStatusText(payout)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(AmountDisplay, {
                    amount: payout.amount()
                })
            }
        ]),
        expandedContent: ()=>/*#__PURE__*/ _jsxs("div", {
                className: styles.expandedWrapper,
                children: [
                    /*#__PURE__*/ _jsx(PayoutFailedCallout, {
                        payout: payout,
                        testId: "".concat(testId, "-failure-reason")
                    }),
                    payout.isPaid() && showQboIntegration && !!payout.qboIntegration() && /*#__PURE__*/ _jsx(PayoutAccountingSection, {
                        reloadPayout: reloadPayout,
                        testId: "".concat(testId, "-qbo-section"),
                        payoutId: payout.id(),
                        payoutIntegration: payout.qboIntegration(),
                        accounting: Accounting.QBO
                    }),
                    payout.isPaid() && showXeroIntegration && !!payout.xeroIntegration() && XERO_INTEGRATION && /*#__PURE__*/ _jsx(PayoutAccountingSection, {
                        reloadPayout: reloadPayout,
                        testId: "".concat(testId, "-xero-section"),
                        payoutId: payout.id(),
                        payoutIntegration: payout.xeroIntegration(),
                        accounting: Accounting.Xero
                    }),
                    /*#__PURE__*/ _jsxs(TableList.Provider, {
                        columnDefs: columnDefs,
                        variant: "minimal",
                        justifyBetween: true,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.expandedHeader,
                                children: /*#__PURE__*/ _jsx(TableList.Header, {
                                    items: headerItems,
                                    color: "transparent"
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                "data-testid": "invoice-rows",
                                children: payout.invoices().map((invoice, idx)=>{
                                    var _payout_qboIntegrationForInvoice, _payout_xeroIntegrationForInvoice;
                                    return /*#__PURE__*/ _jsx(TableList.Row, {
                                        testId: "invoice-row-".concat(invoice.id),
                                        items: compact([
                                            {
                                                value: /*#__PURE__*/ _jsxs("div", {
                                                    className: styles.invoiceNumber,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(TextButton, {
                                                            as: Link,
                                                            testId: "".concat(testId, "-invoice-link-").concat(idx),
                                                            "data-heapid": "payment-invoice-link",
                                                            to: getURLWithFullPageInvoice({
                                                                invoiceId: invoice.id,
                                                                relationshipId: invoice.relationshipId
                                                            }),
                                                            children: invoice.formattedNumber
                                                        }),
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: styles.invoiceStatus,
                                                            children: [
                                                                showQboIntegration && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                                                    testId: "".concat(testId, "-invoice-qbo-").concat(idx),
                                                                    invoiceIntegration: (_payout_qboIntegrationForInvoice = payout.qboIntegrationForInvoice(invoice.id)) === null || _payout_qboIntegrationForInvoice === void 0 ? void 0 : _payout_qboIntegrationForInvoice.integration,
                                                                    accountingType: Accounting.QBO
                                                                }),
                                                                showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                                                                    testId: "".concat(testId, "-invoice-xero-").concat(idx),
                                                                    invoiceIntegration: (_payout_xeroIntegrationForInvoice = payout.xeroIntegrationForInvoice(invoice.id)) === null || _payout_xeroIntegrationForInvoice === void 0 ? void 0 : _payout_xeroIntegrationForInvoice.integration,
                                                                    accountingType: Accounting.Xero
                                                                })
                                                            ]
                                                        })
                                                    ]
                                                })
                                            },
                                            {
                                                value: invoice.type
                                            },
                                            {
                                                value: /*#__PURE__*/ _jsx(Typography, {
                                                    ellipsis: true,
                                                    skin: "secondary",
                                                    children: invoice.customer
                                                })
                                            },
                                            {
                                                value: timeModel.formatDate(invoice.createdOn)
                                            },
                                            {
                                                value: timeModel.formatDate(invoice.dueDate)
                                            },
                                            {
                                                value: /*#__PURE__*/ _jsx(AmountDisplay, {
                                                    amount: invoice.totalAmount
                                                })
                                            },
                                            {
                                                value: /*#__PURE__*/ _jsx(FeesCell, {
                                                    invoice: invoice
                                                })
                                            },
                                            {
                                                value: /*#__PURE__*/ _jsx(AmountDisplay, {
                                                    amount: invoice.depositAmount,
                                                    testId: "deposit-amount-".concat(invoice.id)
                                                })
                                            }
                                        ])
                                    }, invoice.id + invoice.type);
                                })
                            })
                        ]
                    })
                ]
            })
    });
}
